import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CajaService } from 'app/services/caja.service';
import { ProductoService } from 'app/services/producto.service';

@Component({
  selector: 'app-lista-productos-dialogo',
  templateUrl: './lista-productos-dialogo.component.html',
  styleUrls: ['./lista-productos-dialogo.component.css'],
})
export class ListaProductosDialogoComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput') searchInput: ElementRef;

  verSpinner: boolean; 
  displayedColumns: string[] = [];
  dataSource: MatTableDataSource<any>;
  articulos = [];
  verMensaje: boolean;
  lowValue = 0;
  highValue = 6;
  porcentajeBonificacion = 0;
  paginaSiguiente = 1;
  totalArticulos = 10;
  itemXPag = 10;
  search = '';
  lastKeypress: any;

  constructor(
    private cajaService: CajaService,
    private productoService: ProductoService,
    public dialogRef: MatDialogRef<ListaProductosDialogoComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.dataSource = new MatTableDataSource(this.articulos);
    // Asegurar que porcentajeBonificacion tenga un valor válido (0 si es undefined o null)
    this.porcentajeBonificacion = this.data.bonificacion || 0;
    
    // Configurar columnas dinámicamente según el tipo de comprobante
    this.configurarColumnas();
  }

  /**
   * Configura las columnas a mostrar según el tipo de comprobante
   */
  configurarColumnas(): void {
    // Columnas básicas siempre presentes
    this.displayedColumns = ['code', 'codebarras', 'name'];
    
    // Para remitos de compra, no mostrar stock ni precios
    if (!this.esRemitoDeCompra) {
      this.displayedColumns.push('stock', 'price');
    }
  }

  /**
   * Determina si es un tipo de compra basado en el contexto
   */
  esTipoCompra(): boolean {
    // Usar la información del tipo de operación si está disponible
    return this.data.tipoOperacion === 'compras' || 
           this.data.type === 'articulo-proveedor' || 
           !!this.data.supplierId;
  }

  /**
   * Getter para determinar si es un remito de compra
   */
  get esRemitoDeCompra(): boolean {
    return this.data.typeComprobante === 'remito' && this.esTipoCompra();
  }

  ngOnInit(): void {
    this.obtenerArticulos();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.search = filterValue.trim();
    
    // Solo aplicar filtro normal para keyup (filtro en tiempo real)
    if (event.type === 'keyup') {
      this.dataSource.filter = this.search.toLowerCase();
    }
  }

  // Método específico para manejar Enter
  onEnterPressed(event: KeyboardEvent) {
    event.preventDefault(); // Prevenir recarga de página
    event.stopPropagation(); // Evitar propagación del evento
    
    const inputElement = event.target as HTMLInputElement;
    const valorInput = inputElement.value.trim();
    
    // Verificar si el input tiene el patrón cantidad*código (ej: 4*4248314839016)
    if (this.tienePatronCantidad(valorInput)) {
      this.procesarCantidadYCodigo(valorInput);
      return;
    }
    
    // Si no es un patrón de cantidad, verificar si es código de barras
    if (this.esCodigoBarras(valorInput)) {
      this.buscarPorCodigoBarras(valorInput);
      return;
    }
    
    // Solo verificar si hay un artículo en los resultados filtrados y seleccionarlo
    this.verificarYSeleccionarUnicoArticulo();
  }

  /**
   * Verifica si el texto tiene el patrón cantidad*código
   */
  tienePatronCantidad(texto: string): boolean {
    if (!texto || !texto.includes('*')) {
      return false;
    }
    
    const partes = texto.split('*');
    if (partes.length !== 2) {
      return false;
    }
    
    const cantidad = parseInt(partes[0]);
    const codigo = partes[1];
    
    return !isNaN(cantidad) && cantidad > 0 && codigo.length > 0;
  }

  /**
   * Procesa el patrón cantidad*código y busca el producto
   */
  procesarCantidadYCodigo(texto: string) {
    const partes = texto.split('*');
    const cantidad = parseInt(partes[0]);
    const codigo = partes[1];
    
    console.log(`Procesando: cantidad=${cantidad}, código=${codigo}`);
    
    // Primero buscar en los productos ya cargados en la lista actual
    const productoEnLista = this.articulos.find(articulo => 
      articulo.barCode === codigo || articulo.id.toString() === codigo
    );
    
    if (productoEnLista) {
      console.log('Producto encontrado en lista actual:', productoEnLista);
      this.selArticuloConCantidad(productoEnLista, cantidad);
      return;
    }
    
    // Si no está en la lista, buscar por API
    this.verSpinner = true;
    
    // Buscar el producto por código de barras
    this.productoService.getVariantePorCodigoBarras(codigo).subscribe(
      (res) => {
        this.verSpinner = false;
        if (res && res.length > 0) {
          const producto = res[0];
          console.log('Producto encontrado por API:', producto);
          
          // Agregar el producto con la cantidad especificada
          this.selArticuloConCantidad(producto, cantidad);
        } else {
          this.openSnackBar(`No se encontró ningún producto con el código de barras: ${codigo}`);
        }
      },
      (err) => {
        this.verSpinner = false;
        console.error('Error al buscar producto:', err);
        this.openSnackBar(`Error al buscar el producto con código de barras: ${codigo}`);
      }
    );
  }

  // Método para detectar si el texto ingresado parece un código de barras
  esCodigoBarras(texto: string): boolean {
    // Un código de barras típicamente:
    // - Tiene al menos 8 caracteres
    // - Contiene principalmente números
    // - Puede tener algunos caracteres especiales pero no espacios
    if (!texto || texto.length < 8) {
      return false;
    }
    
    // Verificar si contiene principalmente números
    const numeroCaracteres = texto.replace(/[^0-9]/g, '').length;
    const porcentajeNumeros = numeroCaracteres / texto.length;
    
    return porcentajeNumeros >= 0.7; // Si al menos el 70% son números
  }

  page(event) {
    if (event.pageIndex == this.paginaSiguiente) {
      this.paginaSiguiente = event.pageIndex + 1;
    } else {
      this.paginaSiguiente = this.paginaSiguiente - 1;
    }   
    this.obtenerArticulos();
  }

  obtenerArticulos() {
    this.verSpinner = true;
    this.articulos = [];
    
    // Si es tipo 'articulo-proveedor', filtrar por proveedor usando el endpoint original
    if (this.data.type === 'articulo-proveedor' && this.data.supplierId) {
      this.productoService.getVariantesDisponiblesCompraProveedor(this.data.supplierId).subscribe(
        (res) => {
          // La respuesta ya viene en el formato esperado para variantes
          this.articulos = res;
          this.dataSource.data = this.articulos;
          this.verSpinner = false;
          this.totalArticulos = res.length;
          this.dataSource.paginator = this.paginator;
        },
        err => {
          this.verSpinner = false;
          this.openSnackBar('Error al cargar los artículos del proveedor');
        }
      );
    } else {
      // Comportamiento original para todos los artículos - siempre usar el endpoint de disponibles
      this.productoService.getVariantesDisponibleVenta().subscribe(
        (res) => {
          this.articulos = res;
          this.dataSource.data = this.articulos;
          this.verSpinner = false;
          this.totalArticulos = res.total || res.length;
          this.dataSource.paginator = this.paginator;
        },
        err => {
          this.verSpinner = false;
        }
      );
    }
  }

  buscarPorCodigoBarras(codigoBarras: string) {
    if (!codigoBarras || codigoBarras.trim() === '') {
      this.obtenerArticulos();
      return;
    }

    this.verSpinner = true;
    this.productoService.getVariantePorCodigoBarras(codigoBarras).subscribe(
      (res) => {
        if (res && res.length > 0) {
          // Si se encuentra el producto por código de barras, mostrarlo
          this.articulos = res;
          this.dataSource.data = this.articulos;
          this.verSpinner = false;
          this.openSnackBar(`Se encontraron ${res.length} producto(s) con el código de barras: ${codigoBarras}`);
          
          // Si hay exactamente un producto, seleccionarlo automáticamente
          if (res.length === 1) {
            setTimeout(() => {
              this.selArticulo(res[0], -1);
            }, 500); // Pequeño delay para que el usuario vea el mensaje
          }
        } else {
          // Si no se encuentra por código de barras, mostrar mensaje
          this.verSpinner = false;
          this.openSnackBar('No se encontró ningún producto con el código de barras ingresado');
          // Recargar todos los productos
          this.obtenerArticulos();
        }
      },
      (err) => {
        this.verSpinner = false;
        this.openSnackBar('Error al buscar por código de barras. Mostrando todos los productos.');
        // En caso de error, recargar todos los productos
        this.obtenerArticulos();
      }
    );
  }

  limpiarBusqueda(input: HTMLInputElement) {
    input.value = '';
    this.search = '';
    this.dataSource.filter = '';
    this.obtenerArticulos();
  }

  /**
   * Limpia la búsqueda y actualiza la lista eliminando productos ya agregados
   */
  limpiarBusquedaYActualizar() {
    // Limpiar el campo de búsqueda
    this.search = '';
    this.dataSource.filter = '';
    
    // Limpiar el input visualmente usando ViewChild
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    
    // Recargar artículos y filtrar los ya agregados
    this.obtenerArticulosYFiltrarAgregados();
  }

  /**
   * Obtiene todos los artículos y filtra los que ya están agregados al comprobante
   */
  obtenerArticulosYFiltrarAgregados() {
    this.verSpinner = true;
    this.articulos = [];
    
    // Si es tipo 'articulo-proveedor', filtrar por proveedor usando el endpoint original
    if (this.data.type === 'articulo-proveedor' && this.data.supplierId) {
      this.productoService.getVariantesDisponiblesCompraProveedor(this.data.supplierId).subscribe(
        (res) => {
          // Filtrar artículos que ya están en la lista del comprobante
          this.articulos = this.filtrarArticulosYaAgregados(res);
          this.dataSource.data = this.articulos;
          this.verSpinner = false;
          this.totalArticulos = this.articulos.length;
          this.dataSource.paginator = this.paginator;
        },
        err => {
          this.verSpinner = false;
          this.openSnackBar('Error al cargar los artículos del proveedor');
        }
      );
    } else {
      // Comportamiento original para todos los artículos - siempre usar el endpoint de disponibles
      this.productoService.getVariantesDisponibleVenta().subscribe(
        (res) => {
          // Filtrar artículos que ya están en la lista del comprobante
          this.articulos = this.filtrarArticulosYaAgregados(res);
          this.dataSource.data = this.articulos;
          this.verSpinner = false;
          this.totalArticulos = this.articulos.length;
          this.dataSource.paginator = this.paginator;
        },
        err => {
          this.verSpinner = false;
        }
      );
    }
  }

  cancelar(guardar?: boolean) {
    if (guardar) {
      this.dialogRef.close(true);
    } else {
      this.dialogRef.close(false);
    }
  }

  selArticulo(variante: any, indice) {
    console.log('selArticulo called with:', variante);
    console.log('Variante stocks:', variante?.stocks);
    
    if (variante !== undefined) {
      // Solo verificar stock si NO es un comprobante de compras Y NO es un servicio
      if (this.data.type !== 'articulo-proveedor' && !this.esServicio(variante) && !this.hasStock(variante)) {
        console.log('selArticulo: Sin stock - mostrando mensaje');
        this.openSnackBar('El producto no tiene stock disponible');
        return;
      }

      console.log('selArticulo: Procesando producto');
      if (this.comprobarArticuloCargado(variante.id)){
        const index = this.comprobarIndiceArticuloCargado(variante.id);
        this.cajaService.articulosList[index].quantity += 1;
        let subt = 0;
        const cantidadXPrecio =  (variante.price * this.cajaService.articulosList[index].quantity);
        const bonif = (this.porcentajeBonificacion * cantidadXPrecio) / 100;
        subt = (cantidadXPrecio - bonif);
        this.cajaService.articulosList[index].subtotalWithIva = subt;
        this.cajaService.articulosList[index].subtotal = subt;
      } else {
        let subt = 0;
        const bonif = (this.porcentajeBonificacion * variante.price) / 100;
        subt = (variante.price - bonif);
        const unidadesConversion = this.getUnidadesConversion(variante);
        this.cajaService.addArticulo({
          id: variante.id,
          code: `${variante.id}`,
          description: variante.name,
          type: 'Producto',
          unitPrice: variante.price,
          quantity: 1,
          bonus: this.porcentajeBonificacion || 0, // Asegurar que nunca sea undefined
          bonusAmount: 0,
          subtotalWithIva: subt,
          measurementUnitId: 52,
          subtotal: subt,
          conversionUnits: unidadesConversion,
          conversionUnitId: unidadesConversion[0]?.id || 0,
          batch: '',
          variantId: variante.id,
          voucherId: 0
        });
       
      }
      
      // Después de agregar el producto, limpiar búsqueda y actualizar lista
      this.limpiarBusquedaYActualizar();
      
      // Mostrar mensaje de confirmación
      this.openSnackBar(`Producto "${variante.name}" agregado al comprobante`);
    }
  }

   // si la variante no tiene conversionUnits, se le asigna una unidad de conversion por defecto 
   // y debe corresponder a la unidad de medida del producto
  getUnidadesConversion(variante: any) {
    if (variante.article?.conversionUnits.length === 0) {
      const conversionUnit = {
        id: variante.article?.measurementUnitId,
        name: variante.article?.measurementUnitName,
        quantity: 1,
        description: "",
        price: variante.price || 0,
        baseMeasurementUnit: {
          id: variante.article?.baseMeasurementUnitId || 0,
          code: variante.article?.baseMeasurementUnitCode || '',
          name: variante.article?.baseMeasurementUnitName || 'UNIDAD'
        },
        measurementUnit: {
          id: variante.article?.measurementUnitId || 0,
          code: variante.article?.measurementUnitCode || '',
          name: variante.article?.measurementUnitName || 'UNIDAD'
        }
      };
      return [conversionUnit];
    } else {
      return variante.article?.conversionUnits;
    }
  }

  comprobarIndiceArticuloCargado(id: number): number {
    let resul = -1;
    for (let index = 0; index < this.cajaService.articulosList.length; index++) {
      const articulo = this.cajaService.articulosList[index];
      if (articulo.id === id) {
        resul = index;
        break;
      }
    }
    return resul;
  }

  comprobarArticuloCargado(id: number): boolean {
    let resul = false;
    for (let index = 0; index < this.cajaService.articulosList.length; index++) {
      const articulo = this.cajaService.articulosList[index];
      if (articulo.id === id) {
        resul = true;
        break;
      }
    }
    return resul;
  }

  openSnackBar(message: string) {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      verticalPosition: 'top'
    });
  }

  onRowClick(row: any) {
    // Solo verificar stock si NO es un comprobante de compras Y NO es un servicio
    if (this.data.type !== 'articulo-proveedor' && !this.esServicio(row) && !this.hasStock(row)) {
      this.openSnackBar('El producto no tiene stock disponible');
      return;
    }
    
    this.selArticulo(row, -1);
  }

  // Función helper para verificar si un producto tiene stock
  hasStock(row: any): boolean {
    // Si se permite agregar artículos sin stock (presupuestos o remitos de compra), siempre devolver true
    if (this.data.permitirSinStock) {
      return true;
    }
    
    // Si es un servicio, siempre permitir (no requiere stock)
    if (this.esServicio(row)) {
      return true;
    }
    
    // Comportamiento normal: verificar que tenga stock disponible
    return row?.stocks && row.stocks.length > 0 && row.stocks[0]?.quantity > 0;
  }

  getStockQuantity(row: any): number | string {
    // Si es un servicio, retornar 'N/A' o un texto indicativo
    if (this.esServicio(row)) {
      return 'N/A';
    }
    return row?.stocks?.[0]?.quantity || 0;
  }

  // Función helper para verificar si un producto es un servicio
  esServicio(row: any): boolean {
    // El tipo puede estar en row.type o en row.article.type (para variantes)
    const tipo = row?.type || row?.article?.type;
    const tipoLower = tipo?.toLowerCase();
    
    // Si el campo type existe y es "servicio"
    if (tipoLower === 'servicio') {
      return true;
    }
    
    // SOLUCIÓN ALTERNATIVA: Si el tipo no viene del backend, verificar por articleTypeId
    // ArticleTypeId 2 generalmente corresponde a servicios en muchos sistemas ERP
    const articleTypeId = row?.articleTypeId || row?.article?.articleTypeId;
    if (articleTypeId === 2) {
      return true;
    }
    
    // Si no hay supplier/articleSupplier y measurementUnit es "OTRAS UNIDADES" (código 98)
    // podría ser un servicio
    const measurementUnitCode = row?.article?.measurementUnit?.code;
    const noTieneProveedor = !row?.supplier && !row?.articleSupplier;
    if (measurementUnitCode === '98' && noTieneProveedor) {
      return true;
    }
    
    return false;
  }

  // Función para determinar el cursor de la fila
  getRowCursor(row: any): string {
    if (this.esServicio(row) || this.hasStock(row)) {
      return 'pointer';
    }
    return 'not-allowed';
  }

  // Función para determinar si la fila debe mostrarse bloqueada
  shouldShowAsBlocked(row: any): boolean {
    // No bloquear si es un servicio
    if (this.esServicio(row)) {
      return false;
    }
    // Bloquear solo si no tiene stock
    return !this.hasStock(row);
  }

  /**
   * Verifica si hay un solo artículo filtrado y lo selecciona automáticamente
   */
  verificarYSeleccionarUnicoArticulo() {
    // Obtener los datos filtrados del dataSource
    const datosFiltrados = this.dataSource.filteredData || this.dataSource.data;
    
    // Si hay exactamente un artículo en los resultados filtrados
    if (datosFiltrados && datosFiltrados.length === 1) {
      const articulo = datosFiltrados[0];
      console.log('Seleccionando automáticamente artículo único:', articulo);
      
      // Seleccionar el artículo automáticamente
      this.selArticulo(articulo, -1);
    }
  }

  /**
   * Filtra los artículos que ya están agregados al comprobante
   */
  filtrarArticulosYaAgregados(articulos: any[]): any[] {
    if (!this.cajaService.articulosList || this.cajaService.articulosList.length === 0) {
      return articulos;
    }
    
    // Obtener los IDs de los artículos ya agregados
    const idsAgregados = this.cajaService.articulosList.map(item => item.id);
    
    // Filtrar los artículos que no están en la lista de agregados
    return articulos.filter(articulo => !idsAgregados.includes(articulo.id));
  }

  /**
   * Selecciona un artículo con cantidad específica
   */
  selArticuloConCantidad(variante: any, cantidad: number) {
    if (variante !== undefined && cantidad > 0) {
      // Solo verificar stock si NO es un comprobante de compras
      if (this.data.type !== 'articulo-proveedor' && !this.hasStock(variante)) {
        this.openSnackBar('El producto no tiene stock disponible');
        return;
      }
      if (this.comprobarArticuloCargado(variante.id)){
        const index = this.comprobarIndiceArticuloCargado(variante.id);
        this.cajaService.articulosList[index].quantity += cantidad;
        let subt = 0;
        const cantidadXPrecio = (variante.price * this.cajaService.articulosList[index].quantity);
        const bonif = (this.porcentajeBonificacion * cantidadXPrecio) / 100;
        subt = (cantidadXPrecio - bonif);
        this.cajaService.articulosList[index].subtotalWithIva = subt;
        this.cajaService.articulosList[index].subtotal = subt;
      } else {
        let subt = 0;
        const cantidadXPrecio = (variante.price * cantidad);
        const bonif = (this.porcentajeBonificacion * cantidadXPrecio) / 100;
        subt = (cantidadXPrecio - bonif);
        const unidadesConversion = this.getUnidadesConversion(variante);
        this.cajaService.addArticulo({
          id: variante.id,
          code: `${variante.id}`,
          description: variante.name,
          type: 'Producto',
          unitPrice: variante.price,
          quantity: cantidad,
          bonus: this.porcentajeBonificacion || 0,
          bonusAmount: 0,
          subtotalWithIva: subt,
          measurementUnitId: variante.article?.measurementUnitId || 52,
          subtotal: subt,
          conversionUnits: unidadesConversion,
          conversionUnitId: unidadesConversion[0]?.id || 0,
          batch: '',
          variantId: variante.id,
          voucherId: 0
        });
      }
      
      // Después de agregar el producto, limpiar búsqueda y actualizar lista
      this.limpiarBusquedaYActualizar();
      
      // Mostrar mensaje de confirmación
      this.openSnackBar(`${cantidad} unidad(es) de "${variante.name}" agregadas al comprobante`);
    }
  }

}
